import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import type { DragEvent } from 'react';

interface DragAndDropItem {
  id: string;
}

interface UseDragAndDropOptions<T extends DragAndDropItem> {
  items: T[];
  onOrderChange?: (items: T[]) => void;
}

interface UseDragAndDropReturn<T extends DragAndDropItem> {
  displayItems: T[];
  draggedIndex: number | null;
  handleDragStart: (e: DragEvent<HTMLDivElement>, idx: number) => void;
  handleDragEnd: () => void;
  handleDragOver: (e: DragEvent<HTMLDivElement>) => void;
  handleDragEnter: (displayPosition: number) => void;
  draggedItem: (item: T) => boolean;
}

export const useDragAndDrop = <T extends DragAndDropItem>({
  items,
  onOrderChange,
}: UseDragAndDropOptions<T>): UseDragAndDropReturn<T> => {
  const [internalItems, setInternalItems] = useState<T[]>(items ?? []);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [targetIndex, setTargetIndex] = useState<number | null>(null);

  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInternalItems((previousItems) => {
      const nextItems = items ?? [];
      if (
        previousItems.length === nextItems.length &&
        previousItems.every(
          (previousItem, index) => previousItem === nextItems[index],
        )
      ) {
        return previousItems;
      }
      return nextItems;
    });
  }, [items]);

  const displayItems = useMemo(() => {
    if (draggedIndex === null || targetIndex === null) return internalItems;

    const result = internalItems.filter(
      (_item: T, i: number) => i !== draggedIndex,
    );
    result.splice(targetIndex, 0, internalItems[draggedIndex]);
    return result;
  }, [internalItems, draggedIndex, targetIndex]);

  const handleDragStart = useCallback(
    (e: DragEvent<HTMLDivElement>, index: number) => {
      dragNodeRef.current = e.currentTarget;
      setDraggedIndex(index);
      setTargetIndex(index);
      e.dataTransfer.effectAllowed = 'move';

      requestAnimationFrame(() => {
        if (dragNodeRef.current) {
          dragNodeRef.current.style.opacity = '0.4';
        }
      });
    },
    [],
  );

  const handleDragEnd = useCallback(() => {
    if (dragNodeRef.current) {
      dragNodeRef.current.style.opacity = '1';
    }

    if (
      draggedIndex !== null &&
      targetIndex !== null &&
      draggedIndex !== targetIndex
    ) {
      const newOrder = internalItems.filter(
        (_item: T, i: number) => i !== draggedIndex,
      );
      newOrder.splice(targetIndex, 0, internalItems[draggedIndex]);
      setInternalItems(newOrder);
      onOrderChange?.(newOrder);
    }

    dragNodeRef.current = null;
    setDraggedIndex(null);
    setTargetIndex(null);
  }, [draggedIndex, targetIndex, internalItems, onOrderChange]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDragEnter = useCallback(
    (displayPosition: number) => {
      if (draggedIndex === null) return;
      setTargetIndex(displayPosition);
    },
    [draggedIndex],
  );

  const draggedItem = useCallback(
    (item: T) => {
      if (draggedIndex === null) return false;
      return internalItems[draggedIndex]?.id === item.id;
    },
    [draggedIndex, internalItems],
  );

  return {
    displayItems,
    draggedIndex,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragEnter,
    draggedItem,
  };
};
